import React, { useEffect, useState } from "react";
import PhoneInput, { phoneDigitsValid } from "../components/PhoneInput";
import SuritLogo from "../components/brand/SuritLogo";
import {
  CheckCircle2,
  Loader2,
  Plus,
  Trash2,
  MessageSquare,
  Clock,
  Stethoscope,
  Building2,
  ChevronRight,
  Mail,
} from "lucide-react";
import { useDatosAlta } from "../lib/planes";
import { enlaceSoporte } from "../lib/soporte";

const API_BASE = "https://api-scrock-prod.delightfulglacier-4488b9be.westus.azurecontainerapps.io";

// WEB.CL3 (11 Oct 2026, DT.269): quien llega aquí ya es cliente, así que su contacto es SOPORTE
// y va al correo de soporte (antes, el WhatsApp comercial, que contesta el asistente). El correo
// sale de /api/publico/alta (TEN.4); si no llega, no se pinta el botón.
function BotonSoporte({ prefijo }) {
  const correo = useDatosAlta().datos?.contacto_email;
  if (!correo) return null;
  return (
    <a
      href={enlaceSoporte(correo)}
      className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
      data-testid="onboarding-soporte"
    >
      <Mail className="w-4 h-4" />
      {prefijo ? `${prefijo} ` : ""}Escríbenos a {correo}
    </a>
  );
}

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const DAY_ORDER = DAYS;

function to12h(time) {
  if (!time) return "";
  const [hStr, mStr] = time.split(":");
  const h = parseInt(hStr, 10);
  const period = h < 12 ? "am" : "pm";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return mStr === "00" ? `${h12}${period}` : `${h12}:${mStr}${period}`;
}

function buildClinicSchedule(workingDays, openTime, closeTime) {
  if (!workingDays.length || !openTime || !closeTime) return "";
  const sorted = [...workingDays].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  const groups = [];
  let current = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prevIdx = DAY_ORDER.indexOf(sorted[i - 1]);
    const currIdx = DAY_ORDER.indexOf(sorted[i]);
    if (currIdx === prevIdx + 1) {
      current.push(sorted[i]);
    } else {
      groups.push(current);
      current = [sorted[i]];
    }
  }
  groups.push(current);
  const groupStr = groups
    .map((g) => (g.length === 1 ? g[0] : `${g[0]}-${g[g.length - 1]}`))
    .join(", ");
  return `${groupStr} ${to12h(openTime)}-${to12h(closeTime)}`;
}

/* ─── Pantallas de estado ─── */
function ScreenError() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
          <span className="text-2xl">🔗</span>
        </div>
        <h2 className="text-xl font-bold text-slate-900">Link inválido o expirado</h2>
        <p className="text-sm text-slate-500 leading-relaxed">
          {/* WEB.CL3: sin plazo en el texto (el token dura ONBOARDING_EXPIRY_DAYS en agency; no se promete aquí). */}
          El link puede haber expirado o ser incorrecto. Pide un enlace nuevo a soporte.
        </p>
        <BotonSoporte />
      </div>
    </div>
  );
}

function ScreenCompleted() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-blue-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">¡Ya enviaste tus datos!</h2>
        <p className="text-sm text-slate-500 leading-relaxed">
          Estamos configurando tu clínica. Te avisaremos por correo cuando esté lista.
        </p>
        <BotonSoporte prefijo="¿Dudas?" />
      </div>
    </div>
  );
}

function ScreenSuccess() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">¡Listo! Recibimos tus datos.</h2>
        <p className="text-sm text-slate-500 leading-relaxed">
          {/* WEB.CL2 (11 Oct 2026): sin plazo de entrega ni "equipo" (Jesús opera solo). */}
          Con estos datos configuramos tu clínica.
          Te avisaremos por correo cuando esté lista.
        </p>
        <BotonSoporte prefijo="¿Tienes dudas?" />
      </div>
    </div>
  );
}

function ScreenLoading() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="text-sm">Cargando tu formulario…</span>
      </div>
    </div>
  );
}

/* ─── Encabezado de sección ─── */
function SectionHeader({ icon: Icon, title, step }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
        {step}
      </div>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-blue-600" />
        <h2 className="font-semibold text-slate-900 text-base">{title}</h2>
      </div>
    </div>
  );
}

/* ─── Campo reutilizable ─── */
function Field({ label, required, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition";

/* ─── Página principal ─── */
export default function OnboardingForm() {
  const token = new URLSearchParams(window.location.search).get("token");

  const [status, setStatus] = useState("loading"); // loading | error | pending | completed | success
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /* Form state */
  const [form, setForm] = useState({
    clinic_name: "",
    specialty: "",
    doctor_name: "",
    phone: "",
    whatsapp: "",
    address: "",
    working_days: [],
    open_time: "",
    close_time: "",
    walk_ins: "",
    services: [{ name: "", duration_minutes: "", price: "" }],
    welcome_message: "",
    faqs: [{ question: "", answer: "" }],
    additional_notes: "",
  });
  const [errors, setErrors] = useState({});

  /* Carga inicial */
  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    fetch(`${API_BASE}/api/v1/onboarding/${token}`)
      .then((r) => {
        if (r.status === 404 || r.status === 410) {
          setStatus("error");
          return null;
        }
        if (!r.ok) {
          setStatus("error");
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        if (data.status === "completed") {
          setStatus("completed");
        } else {
          setStatus("pending");
        }
      })
      .catch(() => setStatus("error"));
  }, [token]);

  /* Helpers */
  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const toggleDay = (day) => {
    setForm((f) => {
      const days = f.working_days.includes(day)
        ? f.working_days.filter((d) => d !== day)
        : [...f.working_days, day];
      return { ...f, working_days: days };
    });
    setErrors((e) => ({ ...e, working_days: undefined }));
  };

  const updateService = (idx, field, value) => {
    setForm((f) => {
      const services = f.services.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
      return { ...f, services };
    });
  };

  const addService = () =>
    setForm((f) => ({ ...f, services: [...f.services, { name: "", duration_minutes: "", price: "" }] }));

  const removeService = (idx) =>
    setForm((f) => ({ ...f, services: f.services.filter((_, i) => i !== idx) }));

  const updateFaq = (idx, field, value) => {
    setForm((f) => {
      const faqs = f.faqs.map((faq, i) => (i === idx ? { ...faq, [field]: value } : faq));
      return { ...f, faqs };
    });
  };

  const addFaq = () =>
    setForm((f) => ({ ...f, faqs: [...f.faqs, { question: "", answer: "" }] }));

  const removeFaq = (idx) =>
    setForm((f) => ({ ...f, faqs: f.faqs.filter((_, i) => i !== idx) }));

  /* Validación */
  const validate = () => {
    const e = {};
    if (!form.clinic_name.trim()) e.clinic_name = "Requerido";
    if (!form.specialty.trim()) e.specialty = "Requerido";
    if (!form.doctor_name.trim()) e.doctor_name = "Requerido";
    if (!phoneDigitsValid(form.phone, true)) e.phone = form.phone ? "Debe tener 10 dígitos" : "Requerido";
    if (form.working_days.length === 0) e.working_days = "Selecciona al menos un día";
    if (!form.open_time) e.open_time = "Requerido";
    if (!form.close_time) e.close_time = "Requerido";
    if (!form.walk_ins) e.walk_ins = "Requerido";
    const validServices = form.services.filter((s) => s.name.trim());
    if (validServices.length === 0) e.services = "Agrega al menos un servicio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* Envío */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstError = document.querySelector("[data-error]");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      form_data: {
        ...form,
        services: form.services.filter((s) => s.name.trim()),
        faqs: form.faqs.filter((f) => f.question.trim() && f.answer.trim()),
        schedule: buildClinicSchedule(form.working_days, form.open_time, form.close_time),
      },
    };

    try {
      const r = await fetch(`${API_BASE}/api/v1/onboarding/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "Error al enviar. Intenta de nuevo.");
      }
      setStatus("success");
    } catch (err) {
      setSubmitError(err.message || "Error al enviar. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") return <ScreenLoading />;
  if (status === "error") return <ScreenError />;
  if (status === "completed") return <ScreenCompleted />;
  if (status === "success") return <ScreenSuccess />;

  /* status === 'pending' → formulario */
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <SuritLogo size={28} tone="claro" rotulo label="Surit Clínicas" />
          <div className="text-xs text-slate-500">Formulario de configuración</div>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-4">
        <h1 className="text-2xl font-bold text-slate-900">Configura tu clínica</h1>
        <p className="mt-1 text-sm text-slate-500 leading-relaxed">
          Completa este formulario para que configuremos tu agente IA.
          Solo toma 5–10 minutos.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="max-w-2xl mx-auto px-4 space-y-6">

        {/* ── SECCIÓN 1: Tu clínica ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <SectionHeader icon={Building2} title="Tu clínica" step="1" />
          <div className="space-y-4">
            <Field label="Nombre de la clínica" required>
              <input
                type="text"
                className={`${inputCls} ${errors.clinic_name ? "border-red-400 focus:ring-red-400" : ""}`}
                value={form.clinic_name}
                onChange={(e) => set("clinic_name", e.target.value)}
                placeholder="Clínica Santa Cruz"
                data-error={errors.clinic_name ? true : undefined}
              />
              {errors.clinic_name && <p className="text-xs text-red-500 mt-1">{errors.clinic_name}</p>}
            </Field>

            <Field label="Especialidad" required>
              <input
                type="text"
                className={`${inputCls} ${errors.specialty ? "border-red-400 focus:ring-red-400" : ""}`}
                value={form.specialty}
                onChange={(e) => set("specialty", e.target.value)}
                placeholder="Medicina general, Pediatría"
                data-error={errors.specialty ? true : undefined}
              />
              {errors.specialty && <p className="text-xs text-red-500 mt-1">{errors.specialty}</p>}
            </Field>

            <Field label="Nombre del doctor" required>
              <input
                type="text"
                className={`${inputCls} ${errors.doctor_name ? "border-red-400 focus:ring-red-400" : ""}`}
                value={form.doctor_name}
                onChange={(e) => set("doctor_name", e.target.value)}
                placeholder="Dr. Juan Pérez"
                data-error={errors.doctor_name ? true : undefined}
              />
              {errors.doctor_name && <p className="text-xs text-red-500 mt-1">{errors.doctor_name}</p>}
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Teléfono de la clínica" required>
                <PhoneInput
                  value={form.phone}
                  onChange={(digits) => set("phone", digits)}
                  required
                  className={`${inputCls} ${errors.phone ? "border-red-400 focus:ring-red-400" : ""}`}
                  placeholder="9611234567"
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </Field>

              <Field label="WhatsApp de atención" hint="Si es diferente al teléfono">
                <input
                  type="tel"
                  className={inputCls}
                  value={form.whatsapp}
                  onChange={(e) => set("whatsapp", e.target.value)}
                  placeholder="9617654321"
                />
              </Field>
            </div>

            <Field label="Dirección" hint="Opcional — para que el agente la comparta con los pacientes">
              <input
                type="text"
                className={inputCls}
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="Av. Central 123, Tuxtla Gutiérrez, Chiapas"
              />
            </Field>
          </div>
        </div>

        {/* ── SECCIÓN 2: Horarios ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <SectionHeader icon={Clock} title="Horarios" step="2" />
          <div className="space-y-5">
            <Field label="Días de atención" required>
              <div className="flex flex-wrap gap-2 mt-1" data-error={errors.working_days ? true : undefined}>
                {DAYS.map((day) => {
                  const active = form.working_days.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        active
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600"
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              {errors.working_days && <p className="text-xs text-red-500 mt-2">{errors.working_days}</p>}
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Hora de apertura" required>
                <input
                  type="time"
                  className={`${inputCls} ${errors.open_time ? "border-red-400 focus:ring-red-400" : ""}`}
                  value={form.open_time}
                  onChange={(e) => set("open_time", e.target.value)}
                  data-error={errors.open_time ? true : undefined}
                />
                {errors.open_time && <p className="text-xs text-red-500 mt-1">{errors.open_time}</p>}
              </Field>

              <Field label="Hora de cierre" required>
                <input
                  type="time"
                  className={`${inputCls} ${errors.close_time ? "border-red-400 focus:ring-red-400" : ""}`}
                  value={form.close_time}
                  onChange={(e) => set("close_time", e.target.value)}
                  data-error={errors.close_time ? true : undefined}
                />
                {errors.close_time && <p className="text-xs text-red-500 mt-1">{errors.close_time}</p>}
              </Field>
            </div>

            {(() => {
              const preview = buildClinicSchedule(form.working_days, form.open_time, form.close_time);
              return preview ? (
                <p className="text-xs text-slate-500 -mt-2">
                  Tu horario: <span className="font-medium text-slate-700">{preview}</span>
                </p>
              ) : null;
            })()}

            <Field label="¿Acepta pacientes sin cita?" required>
              <div className="flex flex-col sm:flex-row gap-3 mt-1" data-error={errors.walk_ins ? true : undefined}>
                {["Sí acepta", "Solo con cita previa"].map((opt) => {
                  const active = form.walk_ins === opt;
                  return (
                    <label
                      key={opt}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                        active ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="walk_ins"
                        value={opt}
                        checked={active}
                        onChange={() => set("walk_ins", opt)}
                        className="accent-blue-600"
                      />
                      <span className={`text-sm font-medium ${active ? "text-blue-700" : "text-slate-700"}`}>{opt}</span>
                    </label>
                  );
                })}
              </div>
              {errors.walk_ins && <p className="text-xs text-red-500 mt-2">{errors.walk_ins}</p>}
            </Field>
          </div>
        </div>

        {/* ── SECCIÓN 3: Servicios ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <SectionHeader icon={Stethoscope} title="Servicios" step="3" />
          <p className="text-xs text-slate-500 mb-4 -mt-2">
            Lista los servicios que ofreces. El agente IA los usará para responder a los pacientes.
          </p>

          {errors.services && (
            <p className="text-xs text-red-500 mb-3" data-error>{errors.services}</p>
          )}

          <div className="space-y-3">
            {form.services.map((svc, idx) => (
              <div key={idx} className="flex gap-2 items-start p-3 rounded-xl border border-slate-100 bg-slate-50">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="Nombre del servicio *"
                    value={svc.name}
                    onChange={(e) => updateService(idx, "name", e.target.value)}
                  />
                  <input
                    type="number"
                    className={inputCls}
                    placeholder="Duración (min)"
                    value={svc.duration_minutes}
                    onChange={(e) => updateService(idx, "duration_minutes", e.target.value)}
                    min="1"
                  />
                  <input
                    type="number"
                    className={inputCls}
                    placeholder="Precio (MXN)"
                    value={svc.price}
                    onChange={(e) => updateService(idx, "price", e.target.value)}
                    min="0"
                  />
                </div>
                {form.services.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeService(idx)}
                    className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    aria-label="Eliminar servicio"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addService}
            className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <Plus className="w-4 h-4" /> Agregar servicio
          </button>
        </div>

        {/* ── SECCIÓN 4: Tu agente IA ── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <SectionHeader icon={MessageSquare} title="Tu agente IA" step="4" />
          <div className="space-y-5">
            <Field
              label="Mensaje de bienvenida"
              hint="Así saludará el agente a los pacientes en WhatsApp"
            >
              <textarea
                className={inputCls}
                rows={3}
                value={form.welcome_message}
                onChange={(e) => set("welcome_message", e.target.value)}
                placeholder="Hola, bienvenido a Clínica Santa Cruz. Soy Sofi, tu asistente virtual. ¿En qué te puedo ayudar?"
              />
            </Field>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-1">
                Preguntas frecuentes
                <span className="text-slate-400 font-normal ml-1 text-xs">(opcional)</span>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Preguntas que hacen tus pacientes y las respuestas que quieres que el agente dé.
              </p>
              <div className="space-y-3">
                {form.faqs.map((faq, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-3 rounded-xl border border-slate-100 bg-slate-50">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="Pregunta — ¿Cuánto cuesta la consulta?"
                        value={faq.question}
                        onChange={(e) => updateFaq(idx, "question", e.target.value)}
                      />
                      <textarea
                        className={inputCls}
                        rows={2}
                        placeholder="Respuesta — La consulta general cuesta $350 MXN."
                        value={faq.answer}
                        onChange={(e) => updateFaq(idx, "answer", e.target.value)}
                      />
                    </div>
                    {form.faqs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFaq(idx)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        aria-label="Eliminar pregunta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addFaq}
                className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                <Plus className="w-4 h-4" /> Agregar pregunta
              </button>
            </div>

            <Field label="Notas adicionales" hint="Cualquier otra información relevante para tu agente">
              <textarea
                className={inputCls}
                rows={3}
                value={form.additional_notes}
                onChange={(e) => set("additional_notes", e.target.value)}
                placeholder="Ej. No atendemos urgencias. Solo adultos mayores de 18 años…"
              />
            </Field>
          </div>
        </div>

        {/* Error de envío */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {submitError}
          </div>
        )}

        {/* Botón submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-base font-semibold px-6 py-3.5 rounded-2xl shadow-sm transition-colors"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Enviando…
            </>
          ) : (
            <>
              Enviar mis datos
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>

        <p className="text-center text-xs text-slate-400 pb-4">
          Al enviar aceptas que Surit.ai use esta información para configurar tu cuenta.
        </p>
      </form>
    </div>
  );
}
